import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { NotificationToast } from './NotificationToast';
import { parseFechaLocal, formatFechaLocal } from '../../utils/date.utils';
import { API_BASE_URL } from '../../constants/api';
import { User, Users, Calendar, CheckCircle, XCircle, Phone, Mail, Save, X, BookOpen, Plus, Edit2, GraduationCap, Church, Heart, LogOut, Shield, Copy, Check, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import type { Membership } from '../memberships/MembershipsTable';
import { MembershipsService, type DatosComplementarios } from '../../services/memberships/memberships.service';
import { DatosComplementariosForm } from '../memberships/DatosComplementariosForm';

const editProfileSchema = z.object({
  nombres: z.string().min(1, 'Nombres es requerido').min(2, 'Mínimo 2 caracteres'),
  apellidos: z.string().min(1, 'Apellidos es requerido').min(2, 'Mínimo 2 caracteres'),
  fechaNacimiento: z.string().min(1, 'Fecha de nacimiento es requerida'),
  genero: z.enum(['Masculino', 'Femenino']),
  estadoCivil: z.enum(['soltero', 'soltera', 'casado', 'casada', 'viudo', 'viuda', 'divorciado', 'divorciada', 'union_libre']),
  telefono: z.string().min(1, 'Teléfono es requerido').min(7, 'Teléfono inválido'),
  email: z.string().min(1, 'Email es requerido').email('Email inválido'),
  zonaBarrio: z.string().min(1, 'Zona/Barrio es requerido'),
  tipoDocumento: z.enum(['cedula', 'tarjeta_identidad', 'pasaporte', 'otro']).optional(),
  numeroDocumento: z.string().optional(),
  consentimientoDatos: z.boolean(),
  autorizacionImagen: z.boolean(),
  observacionesLegales: z.string().optional()
});

type EditProfileFormData = z.infer<typeof editProfileSchema>;

interface UserProfileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialTab?: 'perfil' | 'adicionales';
  initialEditMode?: boolean;
}

export const UserProfileDrawer: React.FC<UserProfileDrawerProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialTab,
  initialEditMode
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [userMembership, setUserMembership] = useState<Membership | null>(null);
  const { user } = usePermissions();
  const navigate = useNavigate();

  // Valores derivados para el panel izquierdo y tarjetas de información
  const rolesList = (user as any)?.roles || [];
  const activeRoleName = rolesList.find((r: any) => r.activo)?.nombre || rolesList[0]?.nombre || 'Sin rol';
  const isPrivileged = (user as any)?.tipoUsuario === 'administrador';
  const totalPerms = rolesList.reduce(
    (acc: number, r: any) =>
      acc + (r.modulos || []).reduce((a: number, m: any) => a + (m.permisos?.length || 0), 0),
    0,
  );
  const creadoDate = (user as any)?.creadoEn ? new Date((user as any).creadoEn) : null;
  const antiguedadMeses = creadoDate
    ? Math.max(0, Math.floor((Date.now() - creadoDate.getTime()) / (1000 * 60 * 60 * 24 * 30)))
    : 0;

  // Estados para pestañas y datos complementarios
  const [activeTab, setActiveTab] = useState<'perfil' | 'adicionales'>('perfil');
  const [isEditingPerfil, setIsEditingPerfil] = useState(false);
  const [datosComplementarios, setDatosComplementarios] = useState<DatosComplementarios | null>(null);
  const [loadingDatos, setLoadingDatos] = useState(false);
  const [isEditingDatos, setIsEditingDatos] = useState(false);
  const [isSubmittingDatos, setIsSubmittingDatos] = useState(false);
  
  // Estados para notificaciones
  const [showNotification, setShowNotification] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const copyToClipboard = async (value: string, field: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    } catch {
      // Clipboard no disponible: ignorar
    }
  };
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);
  
  // Estados para dropdowns
  const [generoDropdownOpen, setGeneroDropdownOpen] = useState(false);
  const [estadoCivilDropdownOpen, setEstadoCivilDropdownOpen] = useState(false);
  const [tipoDocumentoDropdownOpen, setTipoDocumentoDropdownOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    control,
    setValue
  } = useForm<EditProfileFormData>({
    resolver: zodResolver(editProfileSchema),
    defaultValues: {
      consentimientoDatos: false,
      autorizacionImagen: false
    }
  });

  const fechaNacimiento = watch('fechaNacimiento');

  // Calcular edad si hay fecha de nacimiento
  const calcularEdad = (fecha: string) => {
    if (!fecha) return null;
    const hoy = new Date();
    const nacimiento = parseFechaLocal(fecha);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const mes = hoy.getMonth() - nacimiento.getMonth();
    if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
      edad--;
    }
    return edad;
  };

  const edad = fechaNacimiento ? calcularEdad(fechaNacimiento) : null;

  // Funciones helper para mostrar datos complementarios
  const getNivelEstudiosLabel = (nivel?: string) => {
    const labels: Record<string, string> = {
      'primaria': 'Primaria',
      'secundaria': 'Secundaria',
      'tecnico': 'Técnico/Tecnólogo',
      'universitario': 'Universitario',
      'posgrado': 'Posgrado'
    };
    return labels[nivel || ''] || nivel || 'No especificado';
  };

  const getTiempoAsistenciaLabel = (tiempo?: string) => {
    const labels: Record<string, string> = {
      'menos_1_año': 'Menos de 1 año',
      '1_3_años': '1 a 3 años',
      'mas_3_años': 'Más de 3 años'
    };
    return labels[tiempo || ''] || tiempo || 'No especificado';
  };

  const getTipoPersonalidadLabel = (tipo?: string) => {
    const labels: Record<string, string> = {
      'extrovertido': 'Extrovertido',
      'introvertido': 'Introvertido',
      'cooperativo': 'Cooperativo',
      'intuitivo': 'Intuitivo'
    };
    return labels[tipo || ''] || tipo || 'No especificado';
  };

  // Buscar la membresía del usuario actual desde el perfil combinado
  const loadUserMembership = async () => {
    if (!user?.email) return;
    
    setIsLoadingProfile(true);
    try {
      // Obtener el perfil completo del usuario que incluye información de membresía
      const token = localStorage.getItem('accessToken');
      if (!token) return;
      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Error al obtener el perfil: ${response.status}`);
      }

      const profileData = await response.json();
      
      // Si el usuario tiene información de membresía asociada
      setUserMembership(profileData.data?.usuario?.membership || null);
    } catch (error) {
      console.error('Error al cargar perfil de membresía:', error);
      setUserMembership(null);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  // Cargar datos en el formulario cuando se encuentra la membresía
  useEffect(() => {
    if (userMembership && isOpen) {
      setValue('nombres', userMembership.nombres);
      setValue('apellidos', userMembership.apellidos);
      setValue('fechaNacimiento', userMembership.fechaNacimiento ? userMembership.fechaNacimiento.split('T')[0] : '');
      setValue('genero', userMembership.genero as 'Masculino' | 'Femenino');
      setValue('estadoCivil', userMembership.estadoCivil as any);
      setValue('telefono', userMembership.telefono);
      setValue('email', userMembership.email);
      setValue('zonaBarrio', userMembership.zonaBarrio);
      setValue('tipoDocumento', userMembership.tipoDocumento as any);
      setValue('numeroDocumento', userMembership.numeroDocumento || '');
      setValue('consentimientoDatos', userMembership.consentimientoDatos);
      setValue('autorizacionImagen', userMembership.autorizacionImagen);
      setValue('observacionesLegales', userMembership.observacionesLegales || '');
    }
  }, [userMembership, isOpen, setValue]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      loadUserMembership();
      // Aplicar configuracion inicial si se proporciona
      if (initialTab) {
        setActiveTab(initialTab);
      }
      if (initialEditMode !== undefined) {
        if (initialTab === 'adicionales') {
          setIsEditingDatos(initialEditMode);
        } else {
          setIsEditingPerfil(initialEditMode);
        }
      }
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setIsVisible(false);
      // Reset state when closing
      setTimeout(() => {
        setIsLoading(false);
        setErrorMessage(null);
        setUserMembership(null);
        setActiveTab('perfil');
        setIsEditingPerfil(false);
        setIsEditingDatos(false);
        reset();
      }, 300);
    }
  }, [isOpen, reset, initialTab, initialEditMode]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  // Cargar datos complementarios cuando se selecciona la pestaña
  useEffect(() => {
    if (isVisible && activeTab === 'adicionales' && userMembership?.id) {
      loadDatosComplementarios(userMembership.id);
    }
  }, [isVisible, activeTab, userMembership?.id]);

  const loadDatosComplementarios = async (memberId: string) => {
    setLoadingDatos(true);
    try {
      const datos = await MembershipsService.getDatosComplementarios(memberId);
      setDatosComplementarios(datos);
    } catch (err) {
      console.error('Error cargando datos complementarios:', err);
    } finally {
      setLoadingDatos(false);
    }
  };

  const handleSaveDatosComplementarios = async (data: Partial<DatosComplementarios>) => {
    if (!userMembership?.id) return;
    
    setIsSubmittingDatos(true);
    try {
      if (datosComplementarios) {
        const updated = await MembershipsService.updateDatosComplementarios(userMembership.id, data);
        setDatosComplementarios(updated);
        setNotificationConfig({
          title: '¡Actualización exitosa!',
          message: 'Tus datos complementarios han sido actualizados.',
          type: 'success'
        });
      } else {
        const created = await MembershipsService.createDatosComplementarios(userMembership.id, data);
        setDatosComplementarios(created);
        setNotificationConfig({
          title: '¡Registro exitoso!',
          message: 'Tus datos complementarios han sido guardados.',
          type: 'success'
        });
      }
      setShowNotification(true);
      setIsEditingDatos(false);
      // Notificar a otros componentes (ej: SurveyReminder) que se actualizaron los datos
      window.dispatchEvent(new CustomEvent('datos-complementarios-saved'));
    } catch (err: any) {
      setNotificationConfig({
        title: 'Error al guardar',
        message: err.message || 'No se pudieron guardar los datos.',
        type: 'error'
      });
      setShowNotification(true);
    } finally {
      setIsSubmittingDatos(false);
    }
  };

  const onSubmit = async (data: EditProfileFormData) => {
    if (!userMembership) {
      setNotificationConfig({
        title: 'Error',
        message: 'No se encontró información de membresía para actualizar.',
        type: 'error'
      });
      setShowNotification(true);
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
    
    try {
      const updateData = {
        nombres: data.nombres,
        apellidos: data.apellidos,
        fechaNacimiento: data.fechaNacimiento,
        genero: data.genero,
        estadoCivil: data.estadoCivil,
        telefono: data.telefono,
        email: data.email,
        zonaBarrio: data.zonaBarrio,
        tipoDocumento: data.tipoDocumento,
        numeroDocumento: data.numeroDocumento,
        consentimientoDatos: data.consentimientoDatos,
        autorizacionImagen: data.autorizacionImagen,
        observacionesLegales: data.observacionesLegales
      };

      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No se encontró token de autenticación');
      }

      const response = await fetch(`${API_BASE_URL}/auth/profile/membership`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Error al actualizar el perfil');
      }

      const success = result.success ?? result.data?.success;
      const message = result.message ?? result.data?.message;
      if (!success) {
        throw new Error(message || 'Error al actualizar el perfil');
      }
      
      // Mostrar toast de éxito
      setNotificationConfig({
        title: '¡Perfil actualizado!',
        message: 'Tu información de perfil se ha actualizado exitosamente.',
        type: 'success'
      });
      setShowNotification(true);

      // Cerrar el drawer después de un momento
      setTimeout(() => {
        handleClose();
      }, 2000);
      
      if (onSuccess) {
        onSuccess();
      }
      
    } catch (error) {
      console.error('Error al actualizar perfil:', error);
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido al actualizar perfil';
      setErrorMessage(errorMsg);
      
      setNotificationConfig({
        title: 'Error al actualizar perfil',
        message: errorMsg,
        type: 'error'
      });
      setShowNotification(true);
    } finally {
      setIsLoading(false);
    }
  };

  const generosOptions = [
    { 
      value: 'Masculino', 
      label: 'Masculino',
      description: 'Hombre',
      icon: User,
      color: 'bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100'
    },
    { 
      value: 'Femenino', 
      label: 'Femenino',
      description: 'Mujer',
      icon: User,
      color: 'bg-pink-50 border-pink-200 text-pink-800 hover:bg-pink-100'
    }
  ];

  const generoSeleccionado = watch('genero');

  const estadosCiviles = React.useMemo(() => {
    const base = [
      { value: 'union_libre', label: 'Unión Libre', color: 'bg-indigo-50 border-indigo-200 text-indigo-800 hover:bg-indigo-100' }
    ];

    if (generoSeleccionado === 'Masculino') {
      return [
        { value: 'soltero', label: 'Soltero', color: 'bg-gray-50 border-gray-200 text-gray-800 hover:bg-gray-100' },
        { value: 'casado', label: 'Casado', color: 'bg-green-50 border-green-200 text-green-800 hover:bg-green-100' },
        { value: 'viudo', label: 'Viudo', color: 'bg-purple-50 border-purple-200 text-purple-800 hover:bg-purple-100' },
        { value: 'divorciado', label: 'Divorciado', color: 'bg-orange-50 border-orange-200 text-orange-800 hover:bg-orange-100' },
        ...base
      ];
    } else if (generoSeleccionado === 'Femenino') {
      return [
        { value: 'soltera', label: 'Soltera', color: 'bg-gray-50 border-gray-200 text-gray-800 hover:bg-gray-100' },
        { value: 'casada', label: 'Casada', color: 'bg-green-50 border-green-200 text-green-800 hover:bg-green-100' },
        { value: 'viuda', label: 'Viuda', color: 'bg-purple-50 border-purple-200 text-purple-800 hover:bg-purple-100' },
        { value: 'divorciada', label: 'Divorciada', color: 'bg-orange-50 border-orange-200 text-orange-800 hover:bg-orange-100' },
        ...base
      ];
    } else {
      // Si no hay género seleccionado, mostrar todas las opciones
      return [
        { value: 'soltero', label: 'Soltero', color: 'bg-gray-50 border-gray-200 text-gray-800 hover:bg-gray-100' },
        { value: 'soltera', label: 'Soltera', color: 'bg-gray-50 border-gray-200 text-gray-800 hover:bg-gray-100' },
        { value: 'casado', label: 'Casado', color: 'bg-green-50 border-green-200 text-green-800 hover:bg-green-100' },
        { value: 'casada', label: 'Casada', color: 'bg-green-50 border-green-200 text-green-800 hover:bg-green-100' },
        { value: 'viudo', label: 'Viudo', color: 'bg-purple-50 border-purple-200 text-purple-800 hover:bg-purple-100' },
        { value: 'viuda', label: 'Viuda', color: 'bg-purple-50 border-purple-200 text-purple-800 hover:bg-purple-100' },
        { value: 'divorciado', label: 'Divorciado', color: 'bg-orange-50 border-orange-200 text-orange-800 hover:bg-orange-100' },
        { value: 'divorciada', label: 'Divorciada', color: 'bg-orange-50 border-orange-200 text-orange-800 hover:bg-orange-100' },
        ...base
      ];
    }
  }, [generoSeleccionado]);

  const tiposDocumento = [
    { value: 'cedula', label: 'Cédula de Ciudadanía', color: 'bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100' },
    { value: 'tarjeta_identidad', label: 'Tarjeta de Identidad', color: 'bg-green-50 border-green-200 text-green-800 hover:bg-green-100' },
    { value: 'pasaporte', label: 'Pasaporte', color: 'bg-purple-50 border-purple-200 text-purple-800 hover:bg-purple-100' },
    { value: 'otro', label: 'Otro', color: 'bg-gray-50 border-gray-200 text-gray-800 hover:bg-gray-100' }
  ];

  // Efecto para limpiar estado civil cuando cambia el género y no es compatible
  useEffect(() => {
    const estadoCivilActual = watch('estadoCivil');
    if (generoSeleccionado && estadoCivilActual) {
      const estadosCompatibles = estadosCiviles.map(option => option.value);
      if (!estadosCompatibles.includes(estadoCivilActual)) {
        setValue('estadoCivil', '' as any);
      }
    }
  }, [generoSeleccionado, estadosCiviles, setValue, watch]);

  // Custom Dropdown Component
  const CustomDropdown: React.FC<{
    value: string;
    onValueChange: (value: string) => void;
    options: Array<{
      value: string;
      label: string;
      description?: string;
      icon?: React.ComponentType<any> | string;
      color?: string;
    }>;
    placeholder: string;
    isOpen: boolean;
    onToggle: () => void;
    error?: boolean;
  }> = ({ value, onValueChange, options, placeholder, isOpen, onToggle, error = false }) => {
    const selectedOption = options.find(option => option.value === value);
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          if (isOpen) onToggle();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen, onToggle]);

    return (
      <div ref={dropdownRef} className="relative">
        <button
          type="button"
          onClick={onToggle}
          className={`group w-full px-4 py-3 border rounded-lg transition-all duration-200 text-left flex items-center justify-between shadow-sm ${
            error
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20 bg-red-50/30'
              : isOpen 
                ? 'border-purple-500 focus:border-purple-500 focus:ring-purple-500/20 ring-4 ring-purple-500/20 bg-purple-50/30'
                : 'border-gray-300 focus:border-purple-500 hover:border-purple-400 hover:bg-purple-50/20'
          } focus:outline-none`}
        >
          <div className="flex items-center min-w-0 flex-1">
            {selectedOption ? (
              <>
                {selectedOption.icon && (
                  <div className="mr-3 shrink-0">
                    {typeof selectedOption.icon === 'string' ? (
                      <span className="text-xs">{selectedOption.icon}</span>
                    ) : (
                      <selectedOption.icon className="w-5 h-5" />
                    )}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-900 truncate">{selectedOption.label}</div>
                  {selectedOption.description && (
                    <div className="text-xs text-gray-500 truncate mt-0.5">{selectedOption.description}</div>
                  )}
                </div>
              </>
            ) : (
              <span className="text-gray-500 font-medium">{placeholder}</span>
            )}
          </div>
          <svg 
            className={`w-5 h-5 text-gray-400 transition-all duration-200 shrink-0 ml-2 ${
              isOpen ? 'rotate-180 text-purple-500' : 'group-hover:text-purple-400'
            }`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-auto animate-in slide-in-from-top-2 duration-200">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onValueChange(option.value);
                  onToggle();
                }}
                className={`w-full px-4 py-3 text-left transition-all duration-150 border-b border-gray-50 last:border-b-0 first:rounded-t-xl last:rounded-b-xl ${
                  value === option.value 
                    ? 'bg-purple-100 border-purple-200 shadow-sm' 
                    : option.color || 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center">
                  {option.icon && (
                    <div className="mr-3 shrink-0">
                      {typeof option.icon === 'string' ? (
                        <span className="text-xs">{option.icon}</span>
                      ) : (
                        <option.icon className="w-5 h-5" />
                      )}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className={`font-medium truncate ${
                      value === option.value ? 'text-purple-900' : ''
                    }`}>
                      {option.label}
                    </div>
                    {option.description && (
                      <div className={`text-xs mt-1 truncate ${
                        value === option.value ? 'text-purple-700' : 'opacity-75'
                      }`}>
                        {option.description}
                      </div>
                    )}
                  </div>
                  {value === option.value && (
                    <CheckCircle className="w-5 h-5 text-purple-600 shrink-0 ml-2" />
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-hidden pointer-events-none">
      <div 
        className="absolute inset-0 overflow-hidden pointer-events-auto"
        onClick={handleClose}
      >
        {/* Drawer */}
        <section 
          className={`absolute right-0 top-0 bottom-0 w-full max-w-6xl bg-white border-l border-gray-200 transform transition-transform duration-300 ease-in-out shadow-2xl pointer-events-auto ${
            isVisible ? 'translate-x-0' : 'translate-x-full'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div 
            className="h-full flex flex-col bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 bg-gray-100 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xs font-medium text-gray-900">
                  Configuración de Cuenta
                </h2>
                <button
                  onClick={handleClose}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Body: columna izquierda (resumen de perfil) + columna derecha (pestañas) */}
            <div className="flex-1 flex overflow-hidden">
              {/* COLUMNA IZQUIERDA — resumen del perfil */}
              {userMembership && (
                <aside className="hidden md:flex w-72 lg:w-80 shrink-0 flex-col border-r border-gray-200 overflow-y-auto bg-gray-50/40">
                  <div className="p-6 space-y-6 flex-1">
                    {/* Editar perfil */}
                    <button
                      onClick={() => { setActiveTab('perfil'); setIsEditingPerfil(true); }}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                      Editar Perfil
                    </button>

                    {/* Avatar + identidad */}
                    <div className="text-center">
                      <div className="relative mx-auto w-24 h-24">
                        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center shadow-lg">
                          <span className="text-white text-xl font-medium">
                            {(user as any)?.nombreCompleto?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <span className="absolute bottom-1 right-1 w-4 h-4 bg-green-500 rounded-full ring-2 ring-white" />
                      </div>
                      <h3 className="mt-3 text-xs font-medium text-gray-900">{(user as any)?.nombreCompleto}</h3>
                      <p className="text-xs text-purple-600 font-medium">{activeRoleName}</p>
                      <p className="text-xs text-gray-500 break-all">{(user as any)?.email}</p>
                      <span className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider rounded-full border bg-purple-50 text-purple-700 border-purple-200">
                        <Shield className="w-3 h-3" />
                        {isPrivileged ? 'Privilegios Máximos' : 'Usuario'}
                      </span>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 divide-x divide-gray-200 border border-gray-200 rounded-xl py-3 text-center bg-white">
                      <div>
                        <p className="text-xs font-semibold text-gray-900">{antiguedadMeses} {antiguedadMeses === 1 ? 'mes' : 'meses'}</p>
                        <p className="text-[10px] text-gray-500 uppercase tracking-wide">Antigüedad</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-900">{totalPerms}</p>
                        <p className="text-[10px] text-gray-500 uppercase tracking-wide">Permisos</p>
                      </div>
                    </div>

                    {/* Información básica */}
                    <div>
                      <h4 className="text-[10px] font-medium uppercase tracking-wider text-gray-400 mb-3">
                        Información Básica
                      </h4>
                      <dl className="space-y-2.5 text-xs">
                        <div className="flex justify-between gap-2">
                          <dt className="text-gray-500 shrink-0">Nombre</dt>
                          <dd className="font-medium text-gray-900 text-right truncate">{(user as any)?.nombreCompleto}</dd>
                        </div>
                        <div className="flex justify-between gap-2">
                          <dt className="text-gray-500 shrink-0">Email</dt>
                          <dd className="font-medium text-gray-900 text-right truncate">{(user as any)?.email}</dd>
                        </div>
                        <div className="flex justify-between gap-2">
                          <dt className="text-gray-500 shrink-0">Teléfono</dt>
                          <dd className="font-medium text-gray-900 text-right truncate">{userMembership.telefono || '—'}</dd>
                        </div>
                        <div className="flex justify-between gap-2">
                          <dt className="text-gray-500 shrink-0">Nivel de acceso</dt>
                          <dd className="font-medium text-gray-900 text-right capitalize">{(user as any)?.tipoUsuario}</dd>
                        </div>
                        <div className="flex justify-between gap-2">
                          <dt className="text-gray-500 shrink-0">Estado</dt>
                          <dd className={`font-medium text-right ${(user as any)?.estado ? 'text-green-600' : 'text-red-600'}`}>
                            {(user as any)?.estado ? 'Activa' : 'Inactiva'}
                          </dd>
                        </div>
                        <div className="flex justify-between gap-2">
                          <dt className="text-gray-500 shrink-0">Miembro desde</dt>
                          <dd className="font-medium text-gray-900 text-right">
                            {creadoDate ? formatFechaLocal(creadoDate.toISOString(), { day: '2-digit', month: 'short', year: 'numeric' }, 'es-ES') : '—'}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  </div>

                  {/* Cerrar sesión */}
                  <div className="p-4 border-t border-gray-200">
                    <button
                      onClick={() => {
                        localStorage.removeItem('accessToken');
                        localStorage.removeItem('refreshToken');
                        localStorage.removeItem('userData');
                        navigate('/login');
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Cerrar Sesión
                    </button>
                  </div>
                </aside>
              )}

              {/* COLUMNA DERECHA — pestañas */}
              <div className="flex-1 flex flex-col overflow-hidden">
                {userMembership && (
                  <div className="flex border-b border-gray-200 bg-white px-6">
                    <button
                      onClick={() => { setActiveTab('perfil'); setIsEditingPerfil(false); }}
                      className={`flex items-center px-6 py-3 text-xs font-medium border-b-2 transition-colors ${
                        activeTab === 'perfil'
                          ? 'border-purple-600 text-purple-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <User className="w-4 h-4 mr-2" />
                      Información
                    </button>
                    <button
                      onClick={() => setActiveTab('adicionales')}
                      className={`flex items-center px-6 py-3 text-xs font-medium border-b-2 transition-colors ${
                        activeTab === 'adicionales'
                          ? 'border-purple-600 text-purple-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      Datos Adicionales
                    </button>
                  </div>
                )}

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                  {isLoadingProfile ? (
                <div className="flex items-center justify-center min-h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                  <span className="ml-3 text-gray-600">Cargando información del perfil...</span>
                </div>
              ) : !userMembership ? (
                /* Mensaje cuando no hay membresía */
                <div className="text-center py-12">
                  <div className="mx-auto h-16 w-16 rounded-full bg-yellow-100 flex items-center justify-center mb-4">
                    <Users className="w-8 h-8 text-yellow-600" />
                  </div>
                  <h3 className="text-xs font-medium text-gray-900 mb-2">
                    Sin información de membresía
                  </h3>
                  <p className="text-gray-600 mb-4">
                    No se encontró información de membresía asociada a tu cuenta. 
                    Para completar tu perfil, un administrador debe registrarte como miembro.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                    <p className="text-xs text-blue-800">
                      <strong>¿Qué hacer?</strong><br />
                      Contacta a un administrador para que complete tu registro de membresía y puedas editar tu perfil completo.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {/* TAB: INFORMACIÓN — vista de solo lectura */}
                  {activeTab === 'perfil' && !isEditingPerfil && (
                    <div className="space-y-6">
                      {/* Sobre la Cuenta */}
                      <div className="bg-white border border-gray-200 rounded-xl p-5">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-xs font-medium text-gray-900 flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-purple-600" />
                            Sobre la Cuenta
                          </h4>
                          <button
                            onClick={() => setIsEditingPerfil(true)}
                            className="md:hidden inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                            Editar Perfil
                          </button>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          {(user as any)?.nombreCompleto} es {(user as any)?.tipoUsuario} del sistema con una cuenta{' '}
                          {(user as any)?.estado ? 'activa' : 'inactiva'}
                          {creadoDate
                            ? ` desde el ${formatFechaLocal(creadoDate.toISOString(), { day: '2-digit', month: 'long', year: 'numeric' }, 'es-ES')}`
                            : ''}{' '}
                          ({antiguedadMeses} {antiguedadMeses === 1 ? 'mes' : 'meses'} de antigüedad), con{' '}
                          {rolesList.length} {rolesList.length === 1 ? 'rol' : 'roles'} y {totalPerms} permisos
                          asignados.
                        </p>
                      </div>

                      {/* Información de Contacto */}
                      <div className="bg-white border border-gray-200 rounded-xl p-5">
                        <h4 className="text-xs font-medium text-gray-900 mb-4">Información de Contacto</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-xs">
                          <div className="flex items-center gap-3">
                            <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-[10px] uppercase tracking-wide text-gray-400">Email</p>
                              <p className="font-medium text-gray-900 truncate">{(user as any)?.email}</p>
                            </div>
                            <button
                              onClick={() => copyToClipboard((user as any)?.email, 'email')}
                              className="ml-auto p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                              title="Copiar"
                            >
                              {copiedField === 'email' ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                          <div className="flex items-center gap-3">
                            <Phone className="w-4 h-4 text-gray-400 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-[10px] uppercase tracking-wide text-gray-400">Teléfono</p>
                              <p className="font-medium text-gray-900 truncate">{userMembership.telefono || '—'}</p>
                            </div>
                            {userMembership.telefono && (
                              <button
                                onClick={() => copyToClipboard(userMembership.telefono, 'telefono')}
                                className="ml-auto p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                title="Copiar"
                              >
                                {copiedField === 'telefono' ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                              </button>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <Shield className="w-4 h-4 text-gray-400 shrink-0" />
                            <div>
                              <p className="text-[10px] uppercase tracking-wide text-gray-400">Nivel de Acceso</p>
                              <p className="font-medium text-gray-900 capitalize">{(user as any)?.tipoUsuario}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                            <div>
                              <p className="text-[10px] uppercase tracking-wide text-gray-400">Fecha de Creación</p>
                              <p className="font-medium text-gray-900">
                                {creadoDate ? formatFechaLocal(creadoDate.toISOString(), { day: '2-digit', month: 'long', year: 'numeric' }, 'es-ES') : '—'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <CheckCircle className={`w-4 h-4 shrink-0 ${(user as any)?.estado ? 'text-green-500' : 'text-red-500'}`} />
                            <div>
                              <p className="text-[10px] uppercase tracking-wide text-gray-400">Estado de la Cuenta</p>
                              <p className={`font-medium ${(user as any)?.estado ? 'text-green-600' : 'text-red-600'}`}>
                                {(user as any)?.estado ? 'Activa' : 'Inactiva'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Roles y Permisos */}
                      <div className="bg-white border border-gray-200 rounded-xl p-5">
                        <h4 className="text-xs font-medium text-gray-900 mb-3">Roles y Permisos</h4>
                        <div className="flex flex-wrap gap-2">
                          {rolesList.map((r: any) => (
                            <span
                              key={r.id}
                              className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-lg bg-gray-100 text-gray-700 border border-gray-200"
                            >
                              {r.nombre}
                            </span>
                          ))}
                          <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-lg bg-purple-50 text-purple-700 border border-purple-200">
                            +{totalPerms} permisos
                          </span>
                        </div>
                      </div>

                      {/* Políticas y Consentimientos */}
                      <div className="bg-white border border-gray-200 rounded-xl p-5">
                        <h4 className="text-xs font-medium text-gray-900 mb-4">Políticas y Consentimientos</h4>
                        <div className="space-y-4 text-xs">
                          <div className="flex items-start gap-3">
                            {userMembership.consentimientoDatos ? (
                              <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                            ) : (
                              <XCircle className="w-4 h-4 text-gray-300 shrink-0 mt-0.5" />
                            )}
                            <div>
                              <p className="font-medium text-gray-900">Tratamiento de Datos Personales</p>
                              <p className="text-gray-500">
                                {userMembership.consentimientoDatos
                                  ? 'Aceptó la política de tratamiento y protección de datos personales.'
                                  : 'Aún no acepta la política de tratamiento de datos personales.'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-start gap-3">
                            {userMembership.autorizacionImagen ? (
                              <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                            ) : (
                              <XCircle className="w-4 h-4 text-gray-300 shrink-0 mt-0.5" />
                            )}
                            <div>
                              <p className="font-medium text-gray-900">Uso de Imagen</p>
                              <p className="text-gray-500">
                                {userMembership.autorizacionImagen
                                  ? 'Autorizó el uso de su imagen en actividades y material promocional.'
                                  : 'No autoriza el uso de su imagen en actividades ni material promocional.'}
                              </p>
                            </div>
                          </div>
                          {userMembership.observacionesLegales && (
                            <div className="bg-gray-50 rounded-lg p-3">
                              <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">
                                Observaciones Legales
                              </p>
                              <p className="text-gray-700 whitespace-pre-wrap">
                                {userMembership.observacionesLegales}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* TAB: INFORMACIÓN — edición */}
                  {activeTab === 'perfil' && isEditingPerfil && (
                    <>
                      {/* Errores de validación */}
                      {errorMessage && (
                        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                          <div className="flex">
                            <svg className="w-5 h-5 text-red-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-red-800 text-xs">{errorMessage}</p>
                          </div>
                        </div>
                      )}

                      {/* Formulario */}
                      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {/* Información personal básica */}
                    <div>
                      <h3 className="text-xs font-medium text-gray-900 mb-3">
                        Información Personal
                      </h3>
                      
                      <div className="space-y-4">
                        {/* Nombres y Apellidos */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                              Nombres *
                            </label>
                            <input
                              type="text"
                              {...register('nombres')}
                              placeholder="Juan Carlos"
                              className={`w-full px-4 py-3 border rounded-lg transition-all ${
                                errors.nombres
                                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                  : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                              } focus:ring-4 focus:outline-none`}
                            />
                            {errors.nombres && (
                              <p className="mt-1 text-xs text-red-500">{errors.nombres.message}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                              Apellidos *
                            </label>
                            <input
                              type="text"
                              {...register('apellidos')}
                              placeholder="Pérez González"
                              className={`w-full px-4 py-3 border rounded-lg transition-all ${
                                errors.apellidos
                                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                  : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                              } focus:ring-4 focus:outline-none`}
                            />
                            {errors.apellidos && (
                              <p className="mt-1 text-xs text-red-500">{errors.apellidos.message}</p>
                            )}
                          </div>
                        </div>

                        {/* Fecha de nacimiento y edad calculada */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                              Fecha de Nacimiento *
                            </label>
                            <input
                              type="date"
                              {...register('fechaNacimiento')}
                              className={`w-full px-4 py-3 border rounded-lg transition-all ${
                                errors.fechaNacimiento
                                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                  : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                              } focus:ring-4 focus:outline-none`}
                            />
                            {errors.fechaNacimiento && (
                              <p className="mt-1 text-xs text-red-500">{errors.fechaNacimiento.message}</p>
                            )}
                          </div>

                          {/* Mostrar edad calculada */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                              Edad (calculada)
                            </label>
                            <div className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 flex items-center">
                              <Calendar className="w-5 h-5 text-gray-400 mr-2" />
                              <span className="text-gray-700">
                                {edad !== null ? `${edad} años` : 'Ingresa la fecha de nacimiento'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Género y Estado Civil */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                              Género *
                            </label>
                            <Controller
                              name="genero"
                              control={control}
                              render={({ field }) => (
                                <CustomDropdown
                                  value={field.value || ''}
                                  onValueChange={field.onChange}
                                  options={generosOptions}
                                  placeholder="Seleccionar género..."
                                  isOpen={generoDropdownOpen}
                                  onToggle={() => setGeneroDropdownOpen(!generoDropdownOpen)}
                                  error={!!errors.genero}
                                />
                              )}
                            />
                            {errors.genero && (
                              <p className="mt-1 text-xs text-red-500">{errors.genero.message}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                              Estado Civil *
                            </label>
                            <Controller
                              name="estadoCivil"
                              control={control}
                              render={({ field }) => (
                                <CustomDropdown
                                  value={field.value || ''}
                                  onValueChange={field.onChange}
                                  options={estadosCiviles}
                                  placeholder="Seleccionar estado civil..."
                                  isOpen={estadoCivilDropdownOpen}
                                  onToggle={() => setEstadoCivilDropdownOpen(!estadoCivilDropdownOpen)}
                                  error={!!errors.estadoCivil}
                                />
                              )}
                            />
                            {errors.estadoCivil && (
                              <p className="mt-1 text-xs text-red-500">{errors.estadoCivil.message}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Información de contacto */}
                    <div>
                      <h3 className="text-xs font-medium text-gray-900 mb-3">
                        Información de Contacto
                      </h3>
                      
                      <div className="space-y-4">
                        {/* Teléfono y Email */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                              Teléfono *
                            </label>
                            <div className="relative">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Phone className="h-5 w-5 text-gray-400" />
                              </div>
                              <input
                                type="tel"
                                {...register('telefono')}
                                placeholder="+57 300 123 4567"
                                className={`w-full pl-10 pr-4 py-3 border rounded-lg transition-all ${
                                  errors.telefono
                                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                    : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                                } focus:ring-4 focus:outline-none`}
                              />
                            </div>
                            {errors.telefono && (
                              <p className="mt-1 text-xs text-red-500">{errors.telefono.message}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                              Email *
                            </label>
                            <div className="relative">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Mail className="h-5 w-5 text-gray-400" />
                              </div>
                              <input
                                type="email"
                                {...register('email')}
                                placeholder="usuario@ejemplo.com"
                                className={`w-full pl-10 pr-4 py-3 border rounded-lg transition-all ${
                                  errors.email
                                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                    : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                                } focus:ring-4 focus:outline-none`}
                              />
                            </div>
                            {errors.email && (
                              <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>
                            )}
                          </div>
                        </div>

                        {/* Zona/Barrio */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            Zona/Barrio *
                          </label>
                          <input
                            type="text"
                            {...register('zonaBarrio')}
                            placeholder="Centro, Chapinero, Zona Norte, etc."
                            className={`w-full px-4 py-3 border rounded-lg transition-all ${
                              errors.zonaBarrio
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                            } focus:ring-4 focus:outline-none`}
                          />
                          {errors.zonaBarrio && (
                            <p className="mt-1 text-xs text-red-500">{errors.zonaBarrio.message}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Documentos (opcional) */}
                    <div>
                      <h3 className="text-xs font-medium text-gray-900 mb-3">
                        Documentos de Identidad
                      </h3>
                      
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                              Tipo de Documento
                            </label>
                            <Controller
                              name="tipoDocumento"
                              control={control}
                              render={({ field }) => (
                                <CustomDropdown
                                  value={field.value || ''}
                                  onValueChange={field.onChange}
                                  options={tiposDocumento}
                                  placeholder="Seleccionar tipo..."
                                  isOpen={tipoDocumentoDropdownOpen}
                                  onToggle={() => setTipoDocumentoDropdownOpen(!tipoDocumentoDropdownOpen)}
                                  error={!!errors.tipoDocumento}
                                />
                              )}
                            />
                            {errors.tipoDocumento && (
                              <p className="mt-1 text-xs text-red-500">{errors.tipoDocumento.message}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                              Número de Documento
                            </label>
                            <input
                              type="text"
                              {...register('numeroDocumento')}
                              placeholder="123456789"
                              className={`w-full px-4 py-3 border rounded-lg transition-all ${
                                errors.numeroDocumento
                                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                  : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                              } focus:ring-4 focus:outline-none`}
                            />
                            {errors.numeroDocumento && (
                              <p className="mt-1 text-xs text-red-500">{errors.numeroDocumento.message}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Autorizaciones */}
                    <div>
                      <h3 className="text-xs font-medium text-gray-900 mb-3">
                        Autorizaciones
                      </h3>
                      
                      <div className="space-y-4">
                        {/* Consentimiento de datos */}
                        <div className="flex items-start">
                          <div className="flex items-center h-5 mt-1">
                            <input
                              id="consentimientoDatos"
                              type="checkbox"
                              {...register('consentimientoDatos')}
                              disabled
                              className="w-5 h-5 rounded border-2 border-gray-300 text-purple-600 bg-gray-100 cursor-not-allowed opacity-60"
                            />
                          </div>
                          <div className="ml-3">
                            <label htmlFor="consentimientoDatos" className="text-xs font-medium text-gray-700">
                              Consentimiento para Tratamiento de Datos Personales
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              Autorización otorgada para el tratamiento de datos personales según la ley de protección de datos.
                            </p>
                            <p className="text-xs text-gray-400 mt-1 italic">
                              Este consentimiento no puede ser modificado desde aquí.
                            </p>
                          </div>
                        </div>

                        {/* Autorización de imagen */}
                        <div className="flex items-start">
                          <div className="flex items-center h-5 mt-1">
                            <input
                              id="autorizacionImagen"
                              type="checkbox"
                              {...register('autorizacionImagen')}
                              disabled
                              className="w-5 h-5 rounded border-2 border-gray-300 text-purple-600 bg-gray-100 cursor-not-allowed opacity-60"
                            />
                          </div>
                          <div className="ml-3">
                            <label htmlFor="autorizacionImagen" className="text-xs font-medium text-gray-700">
                              Autorización de Uso de Imagen
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              Autorización para el uso de imagen en actividades y material promocional de la organización.
                            </p>
                            <p className="text-xs text-gray-400 mt-1 italic">
                              Esta autorización no puede ser modificada desde aquí.
                            </p>
                          </div>
                        </div>

                        {/* Observaciones legales */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            Observaciones Legales
                          </label>
                          <textarea
                            {...register('observacionesLegales')}
                            placeholder="Observaciones adicionales sobre consentimientos o restricciones especiales..."
                            rows={3}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-purple-500 focus:ring-purple-500/20 focus:ring-4 focus:outline-none transition-all"
                          />
                        </div>
                      </div>
                    </div>
                  </form>
                </>
              )}

              {/* TAB: DATOS ADICIONALES */}
              {activeTab === 'adicionales' && (
                <div className="space-y-6">
                  {loadingDatos ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                      <span className="ml-3 text-gray-600">Cargando datos adicionales...</span>
                    </div>
                  ) : isEditingDatos ? (
                    /* Formulario de edición */
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-xs font-medium text-gray-900">
                          {datosComplementarios ? 'Editar Datos Adicionales' : 'Agregar Datos Adicionales'}
                        </h4>
                        <button
                          onClick={() => setIsEditingDatos(false)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <DatosComplementariosForm
                        initialData={datosComplementarios}
                        onSubmit={handleSaveDatosComplementarios}
                        onCancel={() => setIsEditingDatos(false)}
                        isSubmitting={isSubmittingDatos}
                      />
                    </div>
                  ) : !datosComplementarios ? (
                    /* Sin datos - mostrar botón para agregar */
                    <div className="text-center py-12">
                      <div className="mx-auto h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                        <BookOpen className="w-8 h-8 text-gray-400" />
                      </div>
                      <h4 className="text-xs font-medium text-gray-900 mb-2">Sin datos adicionales</h4>
                      <p className="text-gray-500 max-w-sm mx-auto mb-4">
                        Aún no tienes datos complementarios registrados.
                      </p>
                      <button
                        onClick={() => setIsEditingDatos(true)}
                        className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Agregar Datos Adicionales
                      </button>
                    </div>
                  ) : (
                    /* Mostrar datos con botón de editar */
                    <>
                      <div className="flex justify-end mb-4">
                        <button
                          onClick={() => setIsEditingDatos(true)}
                          className="inline-flex items-center px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                        >
                          <Edit2 className="w-4 h-4 mr-2" />
                          Editar Información
                        </button>
                      </div>

                      {/* Información Académica y Profesional */}
                      <div>
                        <h4 className="text-xs font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2 flex items-center">
                          <GraduationCap className="w-5 h-5 mr-2 text-purple-600" />
                          Información Académica y Profesional
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-500">Nivel de Estudios</label>
                            <p className="text-gray-900 font-medium">{getNivelEstudiosLabel(datosComplementarios.nivelEstudios)}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500">Profesión/Ocupación</label>
                            <p className="text-gray-900 font-medium">{datosComplementarios.profesion || 'No especificado'}</p>
                          </div>
                        </div>
                      </div>

                      {/* Información Espiritual */}
                      <div>
                        <h4 className="text-xs font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2 flex items-center">
                          <Church className="w-5 h-5 mr-2 text-purple-600" />
                          Información Espiritual
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-500">Tiempo en la Iglesia</label>
                            <p className="text-gray-900 font-medium">{getTiempoAsistenciaLabel(datosComplementarios.tiempoAsistencia)}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500">¿Es Bautizado?</label>
                            <div className="flex items-center mt-1">
                              {datosComplementarios.esBautizado ? (
                                <><CheckCircle className="w-5 h-5 text-green-500 mr-2" /><span className="text-green-700 font-medium">Sí</span></>
                              ) : (
                                <><XCircle className="w-5 h-5 text-red-500 mr-2" /><span className="text-red-700 font-medium">No</span></>
                              )}
                            </div>
                          </div>
                          {datosComplementarios.esBautizado && (
                            <>
                              <div>
                                <label className="block text-xs font-medium text-gray-500">Año de Bautismo</label>
                                <p className="text-gray-900 font-medium">{datosComplementarios.añoBautismo || 'No especificado'}</p>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-500">Iglesia de Bautismo</label>
                                <p className="text-gray-900 font-medium">{datosComplementarios.iglesiaBautismo || 'No especificado'}</p>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-500">Ministro que Bautizó</label>
                                <p className="text-gray-900 font-medium">{datosComplementarios.ministroBautismo || 'No especificado'}</p>
                              </div>
                            </>
                          )}
                          <div>
                            <label className="block text-xs font-medium text-gray-500">¿Oración de Fe?</label>
                            <div className="flex items-center mt-1">
                              {datosComplementarios.oracionFe ? (
                                <><CheckCircle className="w-5 h-5 text-green-500 mr-2" /><span className="text-green-700 font-medium">Sí</span></>
                              ) : (
                                <><XCircle className="w-5 h-5 text-red-500 mr-2" /><span className="text-red-700 font-medium">No</span></>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Estudios Realizados */}
                      {datosComplementarios.estudiosRealizados && datosComplementarios.estudiosRealizados.length > 0 && (
                        <div>
                          <h4 className="text-xs font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2">
                            Estudios Realizados en la Congregación
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {datosComplementarios.estudiosRealizados.map((estudio, index) => (
                              <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {estudio}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Personalidad y Habilidades */}
                      <div>
                        <h4 className="text-xs font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2 flex items-center">
                          <Heart className="w-5 h-5 mr-2 text-purple-600" />
                          Personalidad y Habilidades
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-500">Tipo de Personalidad</label>
                            <p className="text-gray-900 font-medium">{getTipoPersonalidadLabel(datosComplementarios.tipoPersonalidad)}</p>
                          </div>
                        </div>
                        
                        {datosComplementarios.habilidades && datosComplementarios.habilidades.length > 0 && (
                          <div className="mb-4">
                            <label className="block text-xs font-medium text-gray-500 mb-2">Habilidades</label>
                            <div className="flex flex-wrap gap-2">
                              {datosComplementarios.habilidades.map((habilidad, index) => (
                                <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  {habilidad}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {datosComplementarios.dones && datosComplementarios.dones.length > 0 && (
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-2">Dones Espirituales</label>
                            <div className="flex flex-wrap gap-2">
                              {datosComplementarios.dones.map((don, index) => (
                                <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  {don}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Ministerios */}
                      <div>
                        <h4 className="text-xs font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2 flex items-center">
                          <Users className="w-5 h-5 mr-2 text-purple-600" />
                          Participación en Ministerios
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-500">¿Pertenece a algún Ministerio?</label>
                            <div className="flex items-center mt-1">
                              {datosComplementarios.perteneceMinisterio ? (
                                <><CheckCircle className="w-5 h-5 text-green-500 mr-2" /><span className="text-green-700 font-medium">Sí</span></>
                              ) : (
                                <><XCircle className="w-5 h-5 text-red-500 mr-2" /><span className="text-red-700 font-medium">No</span></>
                              )}
                            </div>
                          </div>
                          {datosComplementarios.ministerioDeseado && (
                            <div>
                              <label className="block text-xs font-medium text-gray-500">Ministerio de Interés</label>
                              <p className="text-gray-900 font-medium">{datosComplementarios.ministerioDeseado}</p>
                            </div>
                          )}
                        </div>
                        
                        {datosComplementarios.perteneceMinisterio && datosComplementarios.ministeriosActuales && datosComplementarios.ministeriosActuales.length > 0 && (
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-2">Ministerios Actuales</label>
                            <div className="flex flex-wrap gap-2">
                              {datosComplementarios.ministeriosActuales.map((ministerio, index) => (
                                <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                  {ministerio}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Fecha de la Encuesta */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <label className="block text-xs font-medium text-gray-500">Fecha de la Encuesta</label>
                        <p className="text-gray-900 font-medium">{datosComplementarios.fechaEncuesta ? formatFechaLocal(datosComplementarios.fechaEncuesta, { day: '2-digit', month: '2-digit', year: 'numeric' }, 'es-ES') : 'No especificado'}</p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
            )}
                </div>

                {/* Footer del formulario (solo visible en modo edición) */}
                {userMembership && activeTab === 'perfil' && isEditingPerfil && (
                  <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => setIsEditingPerfil(false)}
                        className="px-3 py-2 text-xs font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        onClick={handleSubmit(onSubmit)}
                        disabled={isLoading}
                        className="flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                      >
                        <Save className="w-4 h-4" />
                        <span>{isLoading ? 'Guardando...' : 'Guardar Cambios'}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
      
      {/* Notification Toast */}
      {notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => {
            setShowNotification(false);
            setNotificationConfig(null);
          }}
          duration={7000}
        />
      )}
    </div>
  );
};

export default UserProfileDrawer;